// Team credit history (RFC-1372 C9) — purchases and spend in one list.
//
// NOT role-gated, and that is the decision rather than an omission. Members can
// already see team spend; hiding the credits-in side gives them a pool whose
// balance they cannot reconcile against anything they are allowed to look at.
// There is no `canViewCreditHistory(role)` branch here for the same reason the
// predicate in permissions.ts takes no argument.
//
// ── What renders as money and what does not ────────────────────────────────
//
// Credits are shown in credits everywhere, per the standing UI rule. The single
// exception is a `purchase` row's amount, which falls inside the existing
// credit-PURCHASE exception: it is what the buyer paid for a top-up, not what a
// generation cost. Spend, refund and grant rows therefore render a credit delta
// and nothing else — no per-model rate, no dollar equivalent, no unit price.
// If a future entry kind carries an amount, it does not get currency by default;
// it gets a decision.
//
// A row with a null actor is a de-identified record left by a deleted account
// (C10). It keeps its amount and its project so the pool still adds up, and it
// shows an anonymous label rather than being dropped from the list — dropping
// it would make the running total disagree with the balance above it.
//
// The ledger is paged, newest first (TEAM_CREDITS_PAGE_SIZE per page), so a
// long-lived team never pulls its whole history in one request. The pager is
// the dashboard's, so both lists number and jump pages the same way.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDownLeft, ArrowUpRight, Loader2 } from 'lucide-react'

import {
  TEAM_CREDITS_ENDPOINT_AVAILABLE,
  TEAM_CREDITS_PAGE_SIZE,
  type TeamCreditEntry,
} from '../../api/teams'
import Pagination from '../dashboard/Pagination'
import { formatCredits, formatMoney } from '../../features/billing/billingTypes'
import { useTeamCredits } from '../../hooks/useTeams'
import { cn } from '../../lib/utils'
import { Card, EmptyState, ErrorText, SecondaryButton, SectionLabel } from './teamsChrome'

function isCreditsIn(entry: TeamCreditEntry): boolean {
  return entry.credits >= 0
}

export default function TeamCreditHistory({ teamId }: { teamId: string }) {
  const { t, i18n } = useTranslation('settings')
  const locale = i18n.language
  // 0-based, newest first. Reset by the parent keying this on teamId.
  const [page, setPage] = useState(0)
  const credits = useTeamCredits(teamId, true, page)
  // The last total we saw, so the pager survives a failed page request
  // (React Query drops the placeholder page on error, taking the total with
  // it) and the user can still step back to a page that loads.
  const [knownTotal, setKnownTotal] = useState(0)
  const total = credits.data?.total
  if (total !== undefined && total !== knownTotal) setKnownTotal(total)
  const totalPages = Math.ceil(knownTotal / TEAM_CREDITS_PAGE_SIZE)
  // Never sit past the last page, e.g. if the ledger shrank under us.
  if (totalPages > 0 && page >= totalPages) setPage(totalPages - 1)
  // A page change keeps the previous page on screen (placeholder data) until
  // the new one arrives. Say so, or the stale page reads as the loaded one.
  const changingPage = credits.isPlaceholderData

  // The endpoint does not exist yet (RFC-1372 has not shipped). Say so rather
  // than rendering a zero balance and an empty list, which would read as "this
  // team has never bought or spent anything" — a false statement about money.
  if (!TEAM_CREDITS_ENDPOINT_AVAILABLE) {
    return (
      <div data-testid="teams-credit-history">
        <SectionLabel>{t('settingsModal.teams.credits')}</SectionLabel>
        <EmptyState testId="teams-credits-unavailable">
          {t('settingsModal.teams.creditsUnavailable')}
        </EmptyState>
      </div>
    )
  }

  return (
    <div data-testid="teams-credit-history">
      <SectionLabel>{t('settingsModal.teams.credits')}</SectionLabel>

      <Card className="mb-3 px-4 py-4">
        <p className="text-sm text-white/60">{t('settingsModal.teams.teamBalance')}</p>
        {credits.isLoading ? (
          <p className="mt-1 text-2xl text-white/40" data-testid="teams-balance-loading">
            {t('settingsModal.teams.loadingBalance')}
          </p>
        ) : credits.isError ? (
          <ErrorText testId="teams-balance-error">
            {t('settingsModal.teams.balanceFailed')}
          </ErrorText>
        ) : (
          <p
            className="text-hero font-light leading-tight text-white"
            data-testid="teams-balance"
          >
            {formatCredits(credits.data?.balance ?? 0, locale)}
          </p>
        )}
      </Card>

      {credits.isLoading ? (
        <EmptyState testId="teams-credit-entries-loading">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin align-[-3px]" aria-hidden="true" />
          {t('settingsModal.teams.loadingHistory')}
        </EmptyState>
      ) : credits.isError ? (
        <Card className="flex items-center justify-between gap-3 px-4 py-3">
          <ErrorText testId="teams-credit-entries-error">
            {t('settingsModal.teams.historyFailed')}
          </ErrorText>
          <SecondaryButton
            testId="teams-credit-entries-retry"
            onClick={() => void credits.refetch()}
          >
            {t('settingsModal.teams.retryHistory')}
          </SecondaryButton>
        </Card>
      ) : page === 0 && (credits.data?.entries.length ?? 0) === 0 ? (
        <EmptyState testId="teams-credit-entries-empty">
          {t('settingsModal.teams.noCreditHistory')}
        </EmptyState>
      ) : (
        <div className="relative" aria-busy={changingPage}>
          <Card
            testId="teams-credit-entries"
            className={cn('transition-opacity', changingPage && 'opacity-40')}
          >
            {(credits.data?.entries ?? []).map((entry, index) => {
              const incoming = isCreditsIn(entry)
              const actor =
                entry.actorDisplayName ??
                (entry.actorUserId === null
                  ? t('settingsModal.teams.formerMember')
                  : t('settingsModal.teams.unknownMember'))
              return (
                <div
                  key={entry.id}
                  data-testid={`teams-credit-entry-${entry.id}`}
                  className={cn(
                    'flex items-center gap-3 px-4 py-2.5',
                    index > 0 && 'border-t border-white/[0.06]',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                      incoming ? 'bg-emerald-400/15 text-emerald-300' : 'bg-white/[0.08] text-white/60',
                    )}
                    aria-hidden="true"
                  >
                    {incoming ? (
                      <ArrowDownLeft className="h-3.5 w-3.5" />
                    ) : (
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    )}
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-white">
                      {t(`settingsModal.teams.entryKind.${entry.kind}`)}
                      {entry.projectName !== null && (
                        <span className="text-white/50">{` · ${entry.projectName}`}</span>
                      )}
                    </p>
                    <p className="truncate text-xs font-medium text-white/45">
                      {t('settingsModal.teams.entryMeta', {
                        actor,
                        date: new Date(entry.occurredAt).toLocaleDateString(locale),
                      })}
                    </p>
                  </div>

                  <div className="shrink-0 text-right">
                    <p
                      className={cn(
                        'text-sm font-medium',
                        incoming ? 'text-emerald-300' : 'text-white/80',
                      )}
                    >
                      {t(
                        incoming
                          ? 'settingsModal.teams.creditsIn'
                          : 'settingsModal.teams.creditsOut',
                        { credits: formatCredits(Math.abs(entry.credits), locale) },
                      )}
                    </p>
                    {/* Currency renders for purchase rows ONLY. See the header. */}
                    {entry.kind === 'purchase' &&
                      entry.amountMinor !== null &&
                      entry.currency !== null && (
                        <p
                          className="text-xs font-medium text-white/45"
                          data-testid={`teams-credit-entry-paid-${entry.id}`}
                        >
                          {formatMoney(
                            { amountMinor: entry.amountMinor, currency: entry.currency },
                            locale,
                          )}
                        </p>
                      )}
                  </div>
                </div>
              )
            })}
          </Card>
          {changingPage && (
            <div
              role="status"
              data-testid="teams-credit-entries-page-loading"
              className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-white/80"
            >
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('settingsModal.teams.loadingHistory')}
            </div>
          )}
        </div>
      )}

      <Pagination
        currentPage={page + 1}
        totalPages={totalPages}
        onPageChange={(next) => setPage(next - 1)}
        testIdPrefix="teams-credit-pagination"
        className="mt-3"
      />
    </div>
  )
}
